import { useState, useEffect } from 'react';
import { Activity, Filter, User, Clock, FileText, Heart, Calendar, Award, Bell, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import apiClient from '@/utils/apiClient';
import { toast } from 'sonner';
import { useAuth } from '@/App';

const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [wardens, setWardens] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    warden_id: '',
    module: ''
  });

  const { user } = useAuth();
  const storedUser = JSON.parse(localStorage.getItem('hostelconnect_user') || '{}');
  const hostelId = user?.hostel_id || user?.hostelId || storedUser?.hostel_id;

  useEffect(() => {
    if (hostelId) {
      fetchWardens();
      fetchActivities();
      fetchStats();
    }
  }, [hostelId]);

  useEffect(() => {
    if (hostelId) {
      fetchActivities();
      fetchStats();
    }
  }, [filters]);

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get(`/v1/admin/wardens/?hostel_id=${hostelId}`);
      setWardens(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch wardens:', error);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      let url = `/v1/admin/activity-logs/?hostel_id=${hostelId}&limit=100`;
      if (filters.warden_id && filters.warden_id !== 'all') url += `&warden_id=${filters.warden_id}`;
      if (filters.module && filters.module !== 'all') url += `&module=${filters.module}`;
      
      const response = await apiClient.get(url);
      setActivities(response.data || []);
    } catch (error) { 
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      let url = `/v1/admin/activity-logs/stats?hostel_id=${hostelId}`;
      if (filters.warden_id && filters.warden_id !== 'all') url += `&warden_id=${filters.warden_id}`;
      
      const response = await apiClient.get(url);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats:', error);
    }
  };

  const getModuleIcon = (module) => {
    const icons = {
      attendance: <Calendar className="w-4 h-4" />,
      health: <Heart className="w-4 h-4" />,
      activities: <Activity className="w-4 h-4" />,
      results: <Award className="w-4 h-4" />,
      announcements: <Bell className="w-4 h-4" />
    };
    return icons[module] || <FileText className="w-4 h-4" />;
  };

  const getModuleColor = (module) => {
    const colors = {
      attendance: 'bg-blue-100 text-blue-700 border-blue-200',
      health: 'bg-red-100 text-red-700 border-red-200',
      activities: 'bg-green-100 text-green-700 border-green-200',
      results: 'bg-purple-100 text-purple-700 border-purple-200',
      announcements: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return colors[module] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getActionLabel = (action) => {
    const labels = {
      'marked_attendance': 'Marked Attendance',
      'bulk_attendance_upload': 'Bulk Attendance Upload',
      'bulk_uploaded_attendance': 'Bulk Attendance Upload',
      'updated_health': 'Updated Health Record',
      'updated_health_record': 'Updated Health Record',
      'added_health_record': 'Added Health Record',
      'bulk_health_upload': 'Bulk Health Upload',
      'bulk_uploaded_health_records': 'Bulk Health Upload',
      'added_activity': 'Added Activity',
      'updated_activity': 'Updated Activity',
      'deleted_activity': 'Deleted Activity',
      'bulk_activity_upload': 'Bulk Activity Upload',
      'bulk_deleted_activities': 'Bulk Deleted Activities',
      'added_result': 'Added Result',
      'added_results': 'Added Results',
      'bulk_results_upload': 'Bulk Results Upload',
      'bulk_uploaded_results': 'Bulk Results Upload',
      'created_announcement': 'Created Notice',
      'updated_announcement': 'Updated Notice',
      'deleted_announcement': 'Deleted Notice',
      'updated_student_details': 'Updated Student Details'
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return mins <= 1 ? 'Just now' : `${mins} mins ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = () => {
    fetchActivities();
    fetchStats();
    toast.success('Activity log refreshed');
  };

  const clearFilters = () => {
    setFilters({ warden_id: '', module: '' });
  };

  const modules = ['attendance', 'health', 'activities', 'results', 'announcements'];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#2C5F7F' }}>Activity Log</h1>
          <p className="text-gray-600 mt-1">Monitor warden activities and updates</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card 
            className={`cursor-pointer transition-all ${!filters.module ? 'ring-2 ring-blue-500 shadow-sm' : 'hover:shadow'}`}
            onClick={() => setFilters(prev => ({ ...prev, module: '' }))}
          >
            <CardContent className="p-4 text-center">
              <Activity className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>{stats.total_activities || 0}</p>
              <p className="text-xs text-gray-500">Total Activities</p>
            </CardContent>
          </Card>
          {modules.map(module => (
            <Card 
              key={module}
              className={`cursor-pointer transition-all ${filters.module === module ? 'ring-2 ring-blue-500 shadow-sm' : 'hover:shadow'}`}
              onClick={() => setFilters(prev => ({ ...prev, module: prev.module === module ? '' : module }))}
            >
              <CardContent className="p-4 text-center">
                <div className={`w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center ${getModuleColor(module)}`}>
                  {getModuleIcon(module)}
                </div>
                <p className="text-xl font-bold" style={{ color: '#2C5F7F' }}>
                  {stats.by_module?.[module]?.count || 0}
                </p>
                <p className="text-xs text-gray-500 capitalize">{module}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <Select 
              value={filters.warden_id || 'all'} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, warden_id: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Wardens" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Wardens</SelectItem>
                {wardens.map(warden => (
                  <SelectItem key={warden.id} value={warden.id}>
                    {warden.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.module || 'all'} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, module: value === 'all' ? '' : value }))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {modules.map(module => (
                  <SelectItem key={module} value={module}>
                    <span className="capitalize">{module}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(filters.warden_id || filters.module) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activity List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Activities
            <span className="text-sm font-normal text-gray-500">
              ({activities.length} {activities.length === 1 ? 'entry' : 'entries'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading activities...</div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No activities found</p>
              {(filters.warden_id || filters.module) && (
                <p className="text-sm mt-1">Try adjusting your filters</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity, index) => (
                <div 
                  key={activity.id || index}
                  className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {/* Module Icon */}
                  <div className={`p-2 rounded-full ${getModuleColor(activity.module)}`}>
                    {getModuleIcon(activity.module)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">
                        {activity.warden_name}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-700">
                        {getActionLabel(activity.action)}
                      </span>
                      {activity.students_affected > 0 && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-600">
                            {activity.students_affected} student{activity.students_affected > 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </div>
                    
                    {activity.details && (
                      <p className="text-sm text-gray-600 mt-1 break-words">
                        {activity.details}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatTimestamp(activity.timestamp)}
                    </div>
                  </div>

                  {/* Module Badge */}
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getModuleColor(activity.module)}`}>
                    {activity.module}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLog;
